import { VFXEffectConfig } from '../../VFXEffectRegistry';
import { ringBlast, slashArc, shardBurst, glintScatter, wispRise, groundCloud, stagedBlast, starFlash, shadowedSlash } from './burstBuilders';
import { ABILITY_SCHOOL_COLORS } from '../../../../shared/constants/AbilitySchoolColors';

type Primitive = VFXEffectConfig['primitives'][number];

/** Clone a built primitive with a tween delay — the stagger idiom for verbs
 *  (groundCloud) that don't take delayMs; the verb stays unedited. */
function delayed(prim: Primitive, ms: number): Primitive {
  return { ...prim, tween: { ...prim.tween!, delay: (prim.tween!.delay ?? 0) + ms } };
}

// =============================================================================
// Reaction VFX — the interaction layer's beats (reactionMatrix.config.ts)
// =============================================================================
//
// Each reaction is a SHORT overlay on top of the normal school hit — it must
// read as "the status responded", not replace the hit. Composed entirely from
// the burst grammar; every def multi-tone (palette lock) and one-shot.

const REACT_SHATTER: VFXEffectConfig = {
  id: 'react_shatter',
  name: 'Shatter (frozen + physical)',
  category: 'burst',
  description: 'The ice shell explodes into shards under the blow',
  screenShake: { intensity: 5, duration: 160 },
  primitives: [
    shardBurst({ count: 8, spread: 20, color: 0xBBE8FF, shard: { points: [0, -8, 3, 0, -3, 0] }, stroke: { color: 0xFFFFFF, width: 1 }, travel: 55, durationMs: 380, glow: true }),
    shardBurst({ count: 5, spread: 12, color: 0xCCF0FF, shard: { width: 5, height: 2 }, travel: 40, durationMs: 300, delayMs: 40 }),
    ringBlast({ radius: 14, color: 0xE8F8FF, alpha: 0.5, expandTo: 2.4, durationMs: 260, stroke: { color: 0xFFFFFF, width: 2 } }),
  ],
};

const REACT_STEAM: VFXEffectConfig = {
  id: 'react_steam',
  name: 'Steam (frozen + fire)',
  category: 'burst',
  description: 'Flash-thaw — a veil of steam rolls up off the target',
  primitives: [
    wispRise({ count: 6, spread: 18, radius: 5, color: 0xDDEEFF, alpha: 0.7, travel: 44, durationMs: 850 }),
    wispRise({ count: 4, spread: 12, radius: 7, color: 0xFFFFFF, alpha: 0.45, travel: 34, durationMs: 1050, delayMs: 200 }),
    groundCloud({ width: 44, height: 14, color: 0xCCDDEE, alpha: 0.35, durationMs: 500 }),
  ],
};

const REACT_QUENCH: VFXEffectConfig = {
  id: 'react_quench',
  name: 'Quench (burn + frost)',
  category: 'burst',
  description: 'The flames die with a hiss — smaller, sadder steam',
  primitives: [
    wispRise({ count: 4, spread: 14, radius: 4, color: 0xCCCCCC, alpha: 0.6, travel: 30, durationMs: 700 }),
    glintScatter({ count: 3, spread: 14, color: 0xFF8844, radius: 2, durationMs: 180 }),
    ringBlast({ radius: 10, color: 0x88AACC, alpha: 0.4, expandTo: 1.8, durationMs: 240 }),
  ],
};

const REACT_WILDFIRE: VFXEffectConfig = {
  id: 'react_wildfire',
  name: 'Wildfire (burn + nature)',
  category: 'burst',
  description: 'The growth catches — a green-gold flare leaps up',
  primitives: [
    ...stagedBlast(
      { radius: 12, color: 0x88CC33, alpha: 0.8, expandTo: 1.9, durationMs: 220, glow: true },
      { radius: 16, color: 0xFF8822, alpha: 0.7, expandTo: 2.3, durationMs: 300, stroke: { color: 0xFFCC44, width: 2 } },
      90,
    ),
    wispRise({ count: 5, spread: 16, radius: 3, color: 0xFFAA33, travel: 40, durationMs: 600, glow: true }),
  ],
};

const REACT_IGNITE: VFXEffectConfig = {
  id: 'react_ignite',
  name: 'Ignite (poison + fire)',
  category: 'burst',
  description: 'The toxins catch light — a sickly green-orange flash',
  primitives: [
    ...stagedBlast(
      { radius: 10, color: 0x99FF44, alpha: 0.85, expandTo: 1.7, durationMs: 180, glow: true },
      { radius: 14, color: 0xFF6622, alpha: 0.75, expandTo: 2.1, durationMs: 260 },
      70,
    ),
    glintScatter({ count: 5, spread: 20, color: 0xCCFF66, durationMs: 240 }),
  ],
};

// Replaced react_conduct (slow+lightning) 2026-07-17: the pair was provably
// dead — no content ability has school 'lightning' (reaction-matrix-expansion
// spec §0). Overgrow is the nature-school rework (spec row 10); `lightning`
// now aliases to `nature` at the matrix boundary.
const REACT_OVERGROW: VFXEffectConfig = {
  id: 'react_overgrow',
  name: 'Overgrow (slow + nature)',
  category: 'burst',
  description: 'Vines whip up out of the mire and seize the slowed target',
  primitives: [
    groundCloud({ width: 48, height: 13, color: 0x4A7A2A, alpha: 0.5, durationMs: 420, stroke: { color: 0x88CC33, width: 1 } }),
    slashArc({ radius: 26, startDeg: 100, endDeg: 190, width: 5, color: 0x55AA22, alpha: 0.9, glow: true, durationMs: 260 }),
    slashArc({ radius: 26, startDeg: 80, endDeg: -10, width: 5, color: 0x77CC44, alpha: 0.9, glow: true, durationMs: 260, fadeDelayMs: 40 }),
    glintScatter({ count: 5, spread: 22, color: 0xCCFF88, radius: 2, durationMs: 240, delayMs: 60 }),
  ],
};

// ── Wave-3 growth (reaction-matrix-expansion spec §1, rows 1-9) ─────────────

const REACT_RESONANCE: VFXEffectConfig = {
  id: 'react_resonance',
  name: 'Resonance (frozen + arcane)',
  category: 'burst',
  description: 'The ice rings like struck crystal — crisp, chime-like, no debris',
  primitives: [
    ...stagedBlast(
      { radius: 12, color: 0x99DDFF, alpha: 0.85, expandTo: 1.8, durationMs: 220, glow: true },
      { radius: 15, color: 0xCC66FF, alpha: 0.7, expandTo: 2.2, durationMs: 280, stroke: { color: 0xFFFFFF, width: 1 } },
      80,
    ),
    starFlash({ innerRadius: 4, outerRadius: 10, color: 0xFFFFFF, alpha: 0.85, durationMs: 240, glow: true }),
  ],
};

const REACT_PRISM: VFXEffectConfig = {
  id: 'react_prism',
  name: 'Prism (frozen + holy)',
  category: 'burst',
  description: 'Light refracts through the frozen shell — white-gold glints off pale ice',
  primitives: [
    glintScatter({ count: 6, spread: 24, color: 0xFFFFFF, durationMs: 240 }),
    glintScatter({ count: 5, spread: 28, color: 0xFFDD44, durationMs: 260, delayMs: 50 }),
    starFlash({ innerRadius: 4, outerRadius: 11, color: 0xFFDD44, alpha: 0.85, durationMs: 300, glow: true }),
    shardBurst({ count: 3, spread: 12, color: 0xBBE8FF, travel: 30, durationMs: 280 }),
  ],
};

const REACT_SCOUR: VFXEffectConfig = {
  id: 'react_scour',
  name: 'Scour (burn + holy)',
  category: 'burst',
  description: 'Holy scours the flame to white ash — gold flash, ash motes, thin gold ring',
  primitives: [
    starFlash({ innerRadius: 4, outerRadius: 10, color: 0xFFDD44, alpha: 0.9, durationMs: 260, glow: true }),
    wispRise({ count: 5, spread: 16, radius: 3, color: 0xE8E4D8, alpha: 0.7, travel: 26, durationMs: 700 }),
    // Stroke-only ring read (outlineOnly verb pending promotion — hand-tuned
    // per spec row 3: near-zero fill, the gold stroke carries the silhouette).
    ringBlast({ radius: 16, color: 0xFFDD44, alpha: 0.15, expandTo: 2.0, durationMs: 320, stroke: { color: 0xFFDD44, width: 2 } }),
  ],
};

const REACT_CLEANSE_FLARE: VFXEffectConfig = {
  id: 'react_cleanse_flare',
  name: 'Cleanse Flare (poison + holy)',
  category: 'burst',
  description: 'Purge flash — the toxin leaves outward; reads as relief, not damage',
  primitives: [
    ringBlast({ radius: 12, color: 0xFFFFFF, alpha: 0.6, expandTo: 2.1, durationMs: 220 }),
    glintScatter({ count: 6, spread: 26, color: 0x44CC44, durationMs: 300 }),
    wispRise({ count: 3, spread: 12, radius: 2.5, color: 0xEEFFEE, alpha: 0.7, travel: 30, durationMs: 650 }),
  ],
};

const REACT_BLIGHT: VFXEffectConfig = {
  id: 'react_blight',
  name: 'Blight (poison + shadow)',
  category: 'burst',
  description: 'The toxin curdles — low, wet, ugly; the tonal opposite of the cleanse',
  primitives: [
    groundCloud({ width: 52, height: 15, color: 0x551188, alpha: 0.5, durationMs: 480 }),
    glintScatter({ count: 4, spread: 18, color: 0x44CC44, radius: 2, durationMs: 240 }),
    delayed(groundCloud({ width: 40, height: 12, color: 0x1B3312, alpha: 0.45, durationMs: 520 }), 120),
  ],
};

const REACT_REPENTANCE: VFXEffectConfig = {
  id: 'react_repentance',
  name: 'Repentance (corruption + holy)',
  category: 'burst',
  description: 'The rot recoils from the light — violet collapse, gold ring, dark motes fleeing',
  primitives: [
    ...stagedBlast(
      { radius: 11, color: 0x551188, alpha: 0.8, expandTo: 1.3, durationMs: 200 },
      { radius: 16, color: 0xFFDD44, alpha: 0.65, expandTo: 2.2, durationMs: 300, stroke: { color: 0xFFEE88, width: 1 } },
      90,
    ),
    wispRise({ count: 4, spread: 14, radius: 2.5, color: 0x331144, alpha: 0.8, travel: 36, durationMs: 600 }),
  ],
};

const REACT_TORMENT_SPIKE: VFXEffectConfig = {
  id: 'react_torment_spike',
  name: 'Torment Spike (agony + physical)',
  category: 'burst',
  description: 'The wracked body jolts — short and violent; the aura owns the sustained read',
  primitives: [
    ...shadowedSlash({
      radius: 24, startDeg: 120, endDeg: 30,
      blade: { color: 0xAA1122, width: 5, glow: true },
      trail: { color: 0x220408, width: 3, delayMs: 50 },
      durationMs: 240,
    }),
    glintScatter({ count: 4, spread: 18, color: 0xFF3344, radius: 2, durationMs: 220 }),
  ],
};

const REACT_DESTABILIZE: VFXEffectConfig = {
  id: 'react_destabilize',
  name: 'Destabilize (unstable_affliction + arcane)',
  category: 'burst',
  description: 'The affliction detonates off-key — magenta-violet staged blast, glowing shards',
  primitives: [
    ...stagedBlast(
      { radius: 11, color: 0xDD22AA, alpha: 0.85, expandTo: 1.8, durationMs: 200, glow: true },
      { radius: 15, color: 0x6644AA, alpha: 0.7, expandTo: 2.2, durationMs: 280 },
      80,
    ),
    shardBurst({ count: 5, spread: 16, color: 0xDD22AA, travel: 44, durationMs: 320, glow: true }),
  ],
};

// Row 9 fallback (spec precondition): no playById tint seam exists yet, so
// curse-amplify ships as 5 generated near-clones — ONE look, per-school glint
// tint from ABILITY_SCHOOL_COLORS (the spec's designated fallback, registry
// cost 5). If the tint seam ever lands, collapse these to one def.
const CURSE_VIOLET = 0x6644AA;
export const CURSE_AMPLIFY_SCHOOLS = ['fire', 'frost', 'arcane', 'shadow', 'nature'] as const;

function curseAmplifyDef(school: (typeof CURSE_AMPLIFY_SCHOOLS)[number]): VFXEffectConfig {
  const tint = ABILITY_SCHOOL_COLORS[school];
  return {
    id: `react_curse_amplify_${school}`,
    name: `Curse Amplify (curse_of_elements + ${school})`,
    category: 'burst',
    description: 'The curse flares as magic strikes it — glints tinted by the incoming school',
    primitives: [
      ringBlast({ radius: 13, color: CURSE_VIOLET, alpha: 0.7, expandTo: 2.0, durationMs: 300, glow: true, stroke: { color: tint, width: 2 } }),
      glintScatter({ count: 6, spread: 22, color: tint, durationMs: 260, delayMs: 40 }),
      starFlash({ innerRadius: 3, outerRadius: 8, color: 0xFFFFFF, alpha: 0.6, durationMs: 200 }),
    ],
  };
}

export const REACTION_VFX: VFXEffectConfig[] = [
  REACT_SHATTER,
  REACT_STEAM,
  REACT_QUENCH,
  REACT_WILDFIRE,
  REACT_IGNITE,
  REACT_OVERGROW,
  REACT_RESONANCE,
  REACT_PRISM,
  REACT_SCOUR,
  REACT_CLEANSE_FLARE,
  REACT_BLIGHT,
  REACT_REPENTANCE,
  REACT_TORMENT_SPIKE,
  REACT_DESTABILIZE,
  ...CURSE_AMPLIFY_SCHOOLS.map(curseAmplifyDef),
];
